/**
 * Сообщить о происшествии.
 *
 * Форма намеренно спрашивает не только «что случилось», но и «что вы
 * сделали»: для няни это защита, а для родителя — половина спокойствия.
 * Кнопка живёт в меню смены, а не на главном экране: не пугать без повода.
 */

import { useState } from 'react'

import { uploadPhoto } from '@/features/media/upload'
import {
  SEVERITY_HINTS,
  SEVERITY_LABELS,
  useReportIncident,
  type IncidentSeverity,
} from '@/shared/api/trust'
import { haptic } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Sheet } from '@/shared/ui/Sheet'
import { Field, TextInput } from '@/shared/ui/fields'
import { ErrorBanner } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

const SEVERITIES: IncidentSeverity[] = ['minor', 'medium', 'serious']

export function IncidentSheet({
  open,
  familyId,
  onClose,
}: {
  open: boolean
  familyId: number
  onClose: () => void
}) {
  const [severity, setSeverity] = useState<IncidentSeverity>('minor')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [action, setAction] = useState('')
  const [mediaIds, setMediaIds] = useState<number[]>([])
  const [uploading, setUploading] = useState(false)

  const reset = () => {
    setSeverity('minor')
    setTitle('')
    setDescription('')
    setAction('')
    setMediaIds([])
  }

  const report = useReportIncident(familyId, {
    onSuccess: () => {
      haptic('success')
      toast.success('Записано, родители получили уведомление')
      reset()
      onClose()
    },
    onError: (error) => toast.error(error.message),
  })

  const canSubmit = title.trim().length > 0 && description.trim().length > 0

  return (
    <Sheet
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title="Что случилось"
      footer={
        <Button
          block
          size="lg"
          loading={report.isPending}
          disabled={!canSubmit}
          onClick={() =>
            report.mutate({
              severity,
              title: title.trim(),
              description: description.trim(),
              action_taken: action.trim() || undefined,
              media_ids: mediaIds,
            })
          }
        >
          Сообщить родителям
        </Button>
      }
    >
      {report.error && <ErrorBanner message={report.error.message} />}

      <p className="text-text-muted text-note leading-snug">
        Записать происшествие — это не признание вины, а нормальная часть работы.
        Зафиксированное вместе снимает вопросы потом.
      </p>

      <div className="mt-4 space-y-2">
        {SEVERITIES.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => {
              haptic('select')
              setSeverity(item)
            }}
            className={
              'press w-full rounded-[var(--radius-button)] px-4 py-3 text-left ' +
              (severity === item
                ? 'bg-accent-soft text-accent'
                : 'bg-surface-muted text-text-muted')
            }
          >
            <span className="block text-body font-medium">{SEVERITY_LABELS[item]}</span>
            <span className="block text-caption opacity-80">{SEVERITY_HINTS[item]}</span>
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-4">
        <Field label="Коротко">
          <TextInput
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Упал с горки"
            maxLength={64}
            autoFocus
          />
        </Field>

        <Field label="Что произошло">
          <TextInput
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Ободрал колено на площадке, плакал пару минут"
            maxLength={500}
          />
        </Field>

        <Field
          label="Что вы сделали"
          hint="Эта строка важна не меньше предыдущей: родитель хочет знать, что уже помогли."
        >
          <TextInput
            value={action}
            onChange={(event) => setAction(event.target.value)}
            placeholder="Промыла, заклеила пластырем, дальше играл спокойно"
            maxLength={300}
          />
        </Field>
      </div>

      <label className="press bg-surface-muted text-text-muted mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-[var(--radius-button)] text-note">
        {uploading ? (
          'Загружаем…'
        ) : mediaIds.length ? (
          `Фото добавлено (${mediaIds.length})`
        ) : (
          <>
            <Icon name="action-attach-photo" size={20} />
            Добавить фото
          </>
        )}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          disabled={uploading || mediaIds.length >= 5}
          onChange={async (event) => {
            const file = event.target.files?.[0]
            if (!file) return
            setUploading(true)
            try {
              const media = await uploadPhoto(file)
              setMediaIds((current) => [...current, media.id])
            } catch {
              toast.error('Фото не загрузилось')
            } finally {
              setUploading(false)
              event.target.value = ''
            }
          }}
        />
      </label>
    </Sheet>
  )
}
